import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Skeleton from "@mui/material/Skeleton";
import Divider from "@mui/material/Divider";
import Toolbar from "@mui/material/Toolbar";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Tooltip from "@mui/material/Tooltip";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import { I18n } from "aws-amplify/utils";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

function AssetsSuggestions(props) {
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchText, setSearchText] = useState("");
  const theme = useTheme();
  const mobileDevice = !useMediaQuery(theme.breakpoints.up("md"));

  const headerCells = [
    {
      id: "id",
      label: I18n.get("ID"),
      align: "left",
      style: { width: mobileDevice ? "90" : "20%" },
      show: false,
    },
    {
      id: "fsl_name_and_serial_number",
      label: I18n.get("Name and SN"),
      style: { width: "40%" },
      align: "left",
      show: !mobileDevice,
    },
    {
      id: "asset_level",
      label: I18n.get("Asset Level"),
      style: { width: "10%" },
      align: "left",
      show: !mobileDevice,
      sortable: true,
    },
    {
      id: "external_id",
      label: I18n.get("External ID"),
      align: "left",
      style: { width: "20%" },
      show: true,
    },
    {
      id: "set",
      label: I18n.get("Set Asset"),
      style: { width: "20%" },
      align: "center",
      show: true,
    },
  ];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const applyFiltering = (value) => {
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      return Object.keys(value).some((key) =>
        value[key]?.toString().toLowerCase().includes(searchTextLower)
      );
    }
    return true;
  };

  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };

  const { state } = props; // Destructure state for easier access
  return (
    <Dialog
      open={props.open}
      onClose={() => props.onClose()}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>{I18n.get("Select Asset")}</DialogTitle>
      <DialogContent>
        <Paper elevation={0} sx={{ padding: "0.5rem" }}>
          {!state || !state.fetched ? ( // Ensure state is defined before accessing it
            <Stack alignItems="center" sx={{ height: "100%" }}>
              <Skeleton
                variant="rounded"
                height={40}
                width="100%"
                sx={{ margin: "2px", marginBottom: "5px" }}
              />
              <Divider sx={{ width: "100%" }} />
              <Skeleton
                variant="rounded"
                height={30}
                width="100%"
                sx={{ margin: "2px", marginTop: "5px" }}
              />
              <Skeleton
                variant="rounded"
                height={30}
                width="100%"
                sx={{ margin: "2px" }}
              />
              <Skeleton
                variant="rounded"
                height={30}
                width="100%"
                sx={{ margin: "2px" }}
              />
            </Stack>
          ) : (
            <Box sx={{ height: "100%", width: "100%" }}>
              <Toolbar
                sx={{
                  justifyContent: "space-between",
                  minHeight: "1rem",
                  ml: -2,
                  pb: 2,
                }}
              >
                <Input
                  type="text"
                  placeholder={I18n.get("Search a Data..")}
                  onChange={searchChanged}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Toolbar>
              <TableContainer>
                <Table size="small" sx={{ height: "100%", width: "100%" }}>
                  <TableHead>
                    <TableRow key="header">
                      {headerCells.map((column, index) => {
                        if (column.show) {
                          return (
                            <TableCell
                              key={index}
                              style={column.style}
                              align={column.align}
                            >
                              {column.label}
                            </TableCell>
                          );
                        }
                        return null; // Change to null to avoid returning empty strings
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.audit // Use optional chaining here to avoid accessing undefined
                      ?.filter(applyFiltering)
                      .sort((a, b) => (a.assetlevel || 0) - (b.assetlevel || 0))
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => (
                        <TableRow key={row.id}>
                          {headerCells.map((column) => {
                            if (column.show) {
                              if (column.id === "set") {
                                return (
                                  <TableCell sx={{ textAlign: "center" }} key={column.id}>
                                    <IconButton
                                      onClick={() => {
                                        props.setAsset(row.id);
                                        props.onClose();
                                      }}
                                    >
                                      <Tooltip arrow title={I18n.get("Set the asset")}>
                                        <ArrowForwardIcon color="primary" />
                                      </Tooltip>
                                    </IconButton>
                                  </TableCell>
                                );
                              } else {
                                return (
                                  <TableCell key={column.id}>
                                    {row[column.id]}
                                  </TableCell>
                                );
                              }
                            }
                            return null; // Change to null for consistency
                          })}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={state.audit.filter(applyFiltering).length || 0} // Safe access to count
                rowsPerPage={rowsPerPage}
                page={page}
                labelRowsPerPage={I18n.get("Rows per page")}
                labelDisplayedRows={({ from, to, count }) => {
                  return `${from}-${to} ${I18n.get("of")} ${count}`;
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
}

export default AssetsSuggestions;
